import { useCallback, useEffect, useRef, useState } from 'react';
import * as THREE from 'three';

interface UseSelectionBoxProps {
  camera: React.MutableRefObject<THREE.PerspectiveCamera | null>;
  mountRef: React.RefObject<HTMLDivElement>;
  scene: React.MutableRefObject<THREE.Scene | null>;
  onUpdateSelection: (meshes: THREE.Mesh[]) => void;
  isCameraControlsEnabled: boolean;
}

interface SelectionBox {
  startX: number;
  startY: number;
  width: number;
  height: number;
}

export const useSelectionBox = ({
  camera,
  mountRef,
  scene,
  onUpdateSelection,
  isCameraControlsEnabled,
}: UseSelectionBoxProps) => {
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionBox, setSelectionBox] = useState<SelectionBox>({
    startX: 0,
    startY: 0,
    width: 0,
    height: 0,
  });

  const startPoint = useRef<{ x: number; y: number } | null>(null);
  const raycaster = useRef(new THREE.Raycaster());
  const onUpdateSelectionRef = useRef(onUpdateSelection);

  useEffect(() => {
    console.log('useSelectionBox - isSelecting changed:', isSelecting);
  }, [isSelecting]);

  useEffect(() => {
    console.log('useSelectionBox - selectionBox changed:', selectionBox);
  }, [selectionBox]);

  useEffect(() => {
    onUpdateSelectionRef.current = onUpdateSelection;
  }, [onUpdateSelection]);

  const isInteractiveObject = useCallback((object: THREE.Object3D): boolean => {
    if (object.name === 'FloorPlane') {
      return false;
    }
    return (
      object instanceof THREE.Mesh &&
      object.userData.isInteractive &&
      !object.userData.isSelectionOverlay &&
      !object.userData.isOverlay
    );
  }, []);

  const getObjectsInSelectionBox = useCallback(
    (box: SelectionBox): THREE.Mesh[] => {
      if (!scene.current || !camera.current || !mountRef.current) return [];

      const selectedObjects: THREE.Mesh[] = [];
      const rect = mountRef.current.getBoundingClientRect();

      // Ensure rect has valid dimensions to prevent division by zero
      if (rect.width === 0 || rect.height === 0) return [];

      // These are canvas-relative pixel coordinates of the selection box edges
      const boxLeftPx = Math.min(box.startX, box.startX + box.width);
      const boxRightPx = Math.max(box.startX, box.startX + box.width);
      const boxTopPx = Math.min(box.startY, box.startY + box.height); // Pixel top (smaller Y value)
      const boxBottomPx = Math.max(box.startY, box.startY + box.height); // Pixel bottom (larger Y value)

      // Convert selection box pixel coordinates to Normalized Device Coordinates (-1 to 1)
      const selectionBoxNDC = {
        left: (boxLeftPx / rect.width) * 2 - 1,
        right: (boxRightPx / rect.width) * 2 - 1,
        top: -(boxTopPx / rect.height) * 2 + 1, // Y is inverted for NDC
        bottom: -(boxBottomPx / rect.height) * 2 + 1, // Y is inverted for NDC
      };

      console.log('Selection Box (Pixels):', {
        left: boxLeftPx,
        right: boxRightPx,
        top: boxTopPx,
        bottom: boxBottomPx,
        rectWidth: rect.width,
        rectHeight: rect.height,
      });
      console.log('Selection Box (NDC):', selectionBoxNDC);

      scene.current.traverse((object) => {
        if (!isInteractiveObject(object)) return;

        const mesh = object as THREE.Mesh;
        const worldPos = new THREE.Vector3();
        worldPos.setFromMatrixPosition(mesh.matrixWorld); // Get world position of the object

        // Project world position to NDC
        const ndcPos = worldPos.clone().project(camera.current!);

        const isInBox =
          ndcPos.x >= selectionBoxNDC.left &&
          ndcPos.x <= selectionBoxNDC.right &&
          ndcPos.y >= selectionBoxNDC.bottom && // NDC bottom is "lower" on screen
          ndcPos.y <= selectionBoxNDC.top && // NDC top is "higher" on screen
          ndcPos.z < 1; // Check if object is in front of the camera

        console.log(
          'Object:',
          mesh.name,
          'WorldPos:',
          worldPos,
          'NDCPos:',
          { x: ndcPos.x, y: ndcPos.y, z: ndcPos.z },
          'InBox:',
          isInBox
        );

        if (isInBox) {
          selectedObjects.push(mesh);
        }
      });

      console.log(
        'useSelectionBox - getObjectsInSelectionBox - selectedObjects:',
        selectedObjects.map((obj) => obj.name)
      );
      return selectedObjects;
    },
    [scene, camera, mountRef, isInteractiveObject]
  );

  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      if (!mountRef.current || isCameraControlsEnabled || e.button !== 0)
        return;

      const rect = mountRef.current.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const ndcX = (mouseX / rect.width) * 2 - 1;
      const ndcY = -(mouseY / rect.height) * 2 + 1;

      if (!camera.current || !scene.current) return;

      raycaster.current.setFromCamera({ x: ndcX, y: ndcY }, camera.current);
      const intersects = raycaster.current.intersectObjects(
        scene.current.children,
        true
      );
      const hitInteractiveObject = intersects.some((intersect) =>
        isInteractiveObject(intersect.object)
      );

      if (!hitInteractiveObject) {
        startPoint.current = { x: mouseX, y: mouseY };
        setIsSelecting(true);
        // This selectionBox state is for the visual overlay
        setSelectionBox({
          startX: mouseX,
          startY: mouseY,
          width: 0,
          height: 0,
        });
        // Clear previous selection when starting a new drag selection
        onUpdateSelectionRef.current([]);
      }
    },
    [
      camera,
      scene,
      mountRef,
      isCameraControlsEnabled,
      isInteractiveObject,
      onUpdateSelectionRef,
    ]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isSelecting || !startPoint.current || !mountRef.current) return;

      const rect = mountRef.current.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const newBox = {
        startX: startPoint.current.x,
        startY: startPoint.current.y,
        width: mouseX - startPoint.current.x,
        height: mouseY - startPoint.current.y,
      };
      // This selectionBox state is for the visual overlay
      setSelectionBox(newBox);

      // Update selection in real-time if box is more than a tiny speck
      if (Math.abs(newBox.width) > 2 || Math.abs(newBox.height) > 2) {
        const currentlySelectedObjects = getObjectsInSelectionBox(newBox);
        onUpdateSelectionRef.current(currentlySelectedObjects);
      }
    },
    [isSelecting, mountRef, getObjectsInSelectionBox, onUpdateSelectionRef]
  );

  const handleMouseUp = useCallback(() => {
    if (!isSelecting || !startPoint.current) {
      // Check startPoint.current as well
      setIsSelecting(false); // Ensure selection state is reset
      return;
    }

    // Final selection based on the box at mouseUp
    // Ensure selectionBox state used here is the most up-to-date for getObjectsInSelectionBox
    // We use the 'selectionBox' state which was updated by the last handleMouseMove
    if (Math.abs(selectionBox.width) > 2 || Math.abs(selectionBox.height) > 2) {
      const finalSelectedObjects = getObjectsInSelectionBox(selectionBox);
      onUpdateSelectionRef.current(finalSelectedObjects);
    } else {
      // If the box is too small, it implies a click or tiny drag,
      // We might want to clear selection or revert to what was selected before mousedown
      // For now, if drag didn't make a substantial box, don't change the selection from mouseMove
      // or if mousedown cleared it, it stays cleared.
      // If mousedown cleared selection, and box is tiny, selection stays empty.
    }

    startPoint.current = null;
    setIsSelecting(false);
  }, [
    isSelecting,
    selectionBox,
    getObjectsInSelectionBox,
    onUpdateSelectionRef,
  ]); // Added onUpdateSelectionRef

  // Add and remove event listeners
  useEffect(() => {
    const mountElement = mountRef.current;
    console.log(
      'useSelectionBox - useEffect for listeners - mountElement:',
      mountElement
    );

    if (!mountElement) return;

    console.log(
      'useSelectionBox - useEffect for listeners: Adding event listeners to mountElement:',
      mountElement
    );

    const mouseDownHandler = (e: MouseEvent) => {
      e.preventDefault();
      handleMouseDown(e);
    };

    const mouseMoveHandler = (e: MouseEvent) => {
      e.preventDefault();
      handleMouseMove(e);
    };

    const mouseUpHandler = (e: MouseEvent) => {
      e.preventDefault();
      handleMouseUp();
    };

    mountElement.addEventListener('mousedown', mouseDownHandler);
    window.addEventListener('mousemove', mouseMoveHandler);
    window.addEventListener('mouseup', mouseUpHandler);

    return () => {
      console.log(
        'useSelectionBox - useEffect for listeners: Removing event listeners from mountElement:',
        mountElement
      );
      mountElement.removeEventListener('mousedown', mouseDownHandler);
      window.removeEventListener('mousemove', mouseMoveHandler);
      window.removeEventListener('mouseup', mouseUpHandler);
    };
  }, [mountRef, handleMouseDown, handleMouseMove, handleMouseUp]);

  return {
    isSelecting,
    selectionBox,
  };
};
